import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Banknote, Clock, CheckCircle2, Loader2, Wallet, RefreshCw, X, Download, Smartphone, Landmark, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerNames = { mtn: "MTN", airtel: "Airtel", zamtel: "Zamtel", card: "Card" };
const networkLabels = { mtn: "MTN Mobile Money", airtel: "Airtel Money", zamtel: "Zamtel Kwacha" };

/** Prominent inline card that tells the super admin exactly where to send
 *  the money. Renders 3 states:
 *   - Mobile money → copper card with network + number + copy button
 *   - Bank         → forest card with bank + holder + account (masked-friendly)
 *   - Unset        → amber warning telling the admin to nudge the host
 */
function PayoutMethodDetails({ pm, testIdPrefix = "payout-method" }) {
  if (!pm) {
    return (
      <div
        data-testid={`${testIdPrefix}-none`}
        className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      >
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <div>
          <b>Host hasn't set a payout destination.</b> Ask them to add MTN / Airtel / Zamtel or bank details in /host/dashboard before you release the funds.
        </div>
      </div>
    );
  }
  const copy = (val, label) => {
    if (!val) return;
    navigator.clipboard?.writeText(val);
  };
  if (pm.method_type === "mobile_money") {
    return (
      <div
        data-testid={`${testIdPrefix}-mobile`}
        className="mt-2 rounded-lg border border-copper/30 bg-copper/5 px-3 py-2 text-xs"
      >
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-copper">
          <Smartphone className="h-3 w-3" /> Send via Mobile Money
        </div>
        <div className="mt-1 text-slate-800">
          <span className="font-semibold">{networkLabels[pm.mm_network] || pm.mm_network}</span>
        </div>
        <button
          type="button"
          onClick={() => copy(pm.mm_number)}
          title="Click to copy the number"
          data-testid={`${testIdPrefix}-copy-mm`}
          className="mt-0.5 block font-mono text-sm font-bold text-slate-900 hover:text-copper"
        >
          {pm.mm_number}
        </button>
      </div>
    );
  }
  return (
    <div
      data-testid={`${testIdPrefix}-bank`}
      className="mt-2 rounded-lg border border-forest/30 bg-forest/5 px-3 py-2 text-xs"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-forest">
        <Landmark className="h-3 w-3" /> Send via Bank Transfer
      </div>
      <div className="mt-1 text-slate-800">
        <div className="font-semibold">{pm.bank_name}</div>
        <div className="text-slate-600">{pm.account_name}</div>
        <button
          type="button"
          onClick={() => copy(pm.account_number)}
          title="Click to copy the account number"
          data-testid={`${testIdPrefix}-copy-acct`}
          className="mt-0.5 block font-mono text-sm font-bold text-slate-900 hover:text-forest"
        >
          {pm.account_number}
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    awaiting_release: { label: "Awaiting release", cls: "bg-amber-100 text-amber-800" },
    released: { label: "Released", cls: "bg-forest/10 text-forest" },
    cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600" },
  };
  const it = map[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${it.cls}`}>{it.label}</span>;
}

export default function AdminPayouts() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const [tab, setTab] = useState("awaiting_release");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [note, setNote] = useState("");
  const [noteFor, setNoteFor] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/payouts`, { params: { status: tab } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const release = async (p) => {
    try {
      setReleasing(p.id);
      const { data } = await axios.post(`${API}/admin/payouts/${p.id}/release`, { note: note.trim() });
      toast.success(
        data.host_notified
          ? `Released K${p.amount_zmw.toFixed(2)} to ${p.host_email} — host notified`
          : `Released K${p.amount_zmw.toFixed(2)} to ${p.host_email}`
      );
      setNote("");
      setNoteFor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    } finally {
      setReleasing(null);
    }
  };

  const cancelPayout = async (p) => {
    if (!window.confirm(
      `Cancel this awaiting payout of K${p.amount_zmw.toFixed(2)} for ${p.host_email}? The host will NOT be paid. Use this for test data or reversed bookings.`
    )) return;
    try {
      setCancelling(p.id);
      await axios.post(`${API}/admin/payouts/${p.id}/cancel`);
      toast.success(`Payout cancelled`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(null);
    }
  };

  const totalAwaiting = useMemo(
    () => items.filter((p) => p.status === "awaiting_release").reduce((s, p) => s + (p.amount_zmw || 0), 0),
    [items]
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-payouts">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Payouts</div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Host payouts</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            When a guest confirms check-in, the paid amount waits here for your manual release to the host. Nothing is
            released automatically.
          </p>
        </div>
        {tab === "awaiting_release" && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-right" data-testid="payouts-summary">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Awaiting release</div>
            <div className="mt-1 font-display text-2xl font-bold text-amber-900">K{totalAwaiting.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Status tabs */}
      <div className="mb-4 flex gap-2">
        {[
          { key: "awaiting_release", label: "Awaiting release", icon: Clock },
          { key: "released", label: "Released", icon: CheckCircle2 },
        ].map((s) => (
          <button
            key={s.key}
            data-testid={`payouts-tab-${s.key}`}
            onClick={() => setTab(s.key)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
              tab === s.key ? "bg-copper text-white" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <s.icon className="h-4 w-4" />
            {s.label}
          </button>
        ))}
        <button
          onClick={load}
          data-testid="payouts-refresh"
          className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:border-copper hover:text-copper"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
        <a
          href={`${API}/admin/payouts/export.csv?status=${tab}`}
          data-testid="payouts-csv-download"
          title="Download released payouts as CSV for accounting reconciliation"
          className="flex items-center gap-1.5 rounded-full border border-forest/20 bg-forest px-3 py-2 text-xs font-semibold text-white hover:bg-forest/90"
        >
          <Download className="h-3.5 w-3.5" /> Download CSV
        </a>
      </div>

      {/* Payouts table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-shadow">
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-3">Booking</th>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Guest check-in</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading payouts…
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">
                {tab === "awaiting_release" ? "No payouts awaiting release. All caught up." : "No released payouts yet."}
              </td></tr>
            ) : items.map((p) => (
              <tr key={p.id} data-testid={`payout-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{p.property_title}</div>
                  <div className="text-xs text-slate-500">Booking · {p.booking_id.slice(0, 8)}… · {providerNames[p.payment_method] || p.payment_method}</div>
                  {p.guest_name && <div className="mt-0.5 text-xs text-slate-500">Guest: {p.guest_name}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-slate-800">{p.host_email || "—"}</div>
                  {p.host_phone && <div className="text-xs text-slate-500">{p.host_phone}</div>}
                  <PayoutMethodDetails pm={p.host_payout_method} testIdPrefix={`payout-dest-${p.id}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-slate-800">{new Date(p.checked_in_at).toLocaleString()}</div>
                  <div className="text-xs text-slate-500">via {p.checked_in_by_role}</div>
                </td>
                <td className="px-4 py-3 text-right font-display text-base font-bold text-slate-900">
                  K{p.amount_zmw.toFixed(2)}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={p.status} />
                  {p.released_at && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      {new Date(p.released_at).toLocaleString()}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {p.status === "awaiting_release" ? (
                    noteFor === p.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <input
                          data-testid={`payout-note-${p.id}`}
                          type="text"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Note (optional)"
                          className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs outline-none focus:border-copper focus:bg-white"
                        />
                        <button
                          onClick={() => release(p)}
                          disabled={releasing === p.id}
                          data-testid={`payout-confirm-${p.id}`}
                          className="btn-primary flex items-center gap-1 text-xs !py-1.5 !px-3 disabled:opacity-50"
                        >
                          {releasing === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Banknote className="h-3 w-3" />}
                          Mark as Paid
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setNoteFor(p.id); setNote(""); }}
                          data-testid={`payout-release-${p.id}`}
                          className="btn-primary flex items-center gap-1 text-xs !py-1.5 !px-3"
                        >
                          <Wallet className="h-3 w-3" /> Mark as Paid
                        </button>
                        {isSuper && (
                          <button
                            onClick={() => cancelPayout(p)}
                            disabled={cancelling === p.id}
                            data-testid={`payout-cancel-${p.id}`}
                            title="Cancel this payout (test data / reversed bookings)"
                            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:border-rose-400 hover:text-rose-600 disabled:opacity-50"
                          >
                            {cancelling === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                            Cancel
                          </button>
                        )}
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-slate-400">{p.released_note ? `“${p.released_note}”` : "—"}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
